import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  Pagination,
  PaginationItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
  downloadReport,
  getDocument,
  getReportHistory,
  uploadProofOfPayment,
} from "../../../../services/dashboard";
import { useToast } from "../../../../hooks/useToast";
import Loader from "../../../../components/Loader";
import ButtonComp from "../../../../components/Button";
import Input from "../../../../components/Input";
import theme from "../../../../theme";
import dayjs from "dayjs";
import { useAuthStore } from "../../../../store/useAuthStore";
import { toast } from "react-toastify";
import { BulkPaymentPopup } from "../../../../components/BulkPaymentPopup";

export default function PaymentHistory() {
  const { userId } = useAuthStore();

  const [reports, setReports] = useState([]);
  const [reportType, setReportType] = useState("");
  const [type, setType] = useState("");
  const [reportsTotalCount, setReportsTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [date, setDate] = useState("");
  const [noData, setNoData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadingBatch, setUploadingBatch] = useState<string | null>(null);
  const [showBulkPayment, setShowBulkPayment] = useState(false);
  const [popFileName, setPopFileName] = useState("No file chosen");
  const [selectedBatchNo, setSelectedBatchNo] = useState<string | null>(null);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filterMode, setFilterMode] = useState("single");
  const [isDataFiltered, setIsDataFiltered] = useState(false);

  const { showInfoToast } = useToast();

  const handleReportDownload = async (fileUrl: any) => {
    try {
      const response = await downloadReport(fileUrl);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileUrl.split("/").pop() || "report.csv";
      link.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Failed to download report!");
    }
  };

  const fetchReportHistory = async (currentPage = page) => {
    try {
      setIsLoading(true);

      const typeParam =
        reportType === "ALL" || !reportType ? undefined : reportType;

      let dateParam: string | undefined;
      let startDateParam: string | undefined;
      let endDateParam: string | undefined;
      let todayParam: boolean | undefined;
      console.log(filterMode, "filtermode");

      if (filterMode === "today") {
        todayParam = true;
      } else if (filterMode === "single") {
        dateParam = date || undefined;
      } else if (filterMode === "range") {
        startDateParam = startDate || undefined;
        endDateParam = endDate || undefined;
      }

      const response = await getReportHistory(
        currentPage,
        pageSize,
        typeParam,
        dateParam,
        startDateParam,
        endDateParam,
        todayParam,
      );

      if (response.status === 200) {
        setReports(response.data.reports);
        setReportsTotalCount(response.data.totalRecords);
        setNoData(false);
      } else {
        setNoData(true);
      }
    } catch (error) {
      showInfoToast("Failed to fetch report history");
    } finally {
      setIsLoading(false);
    }
  };

  const callNextPage = (page: any) => {
    let updatedPage = page - 1;
    setPage(updatedPage);
    fetchReportHistory(updatedPage);
  };

  const getPopFile = (value: any) => {
    setPopFileName(value);
  };

  const handleFileUpload = async (batchNo: string, file: File) => {
    try {
      setUploadingBatch(batchNo);
      setIsLoading(true);
      const response = await uploadProofOfPayment(batchNo, userId, file);
      if (response.status === 200) {
        toast.success("Proof of Payment uploaded successfully!");
        fetchReportHistory(page);
      } else {
        showInfoToast(response.data?.message || "Upload failed!");
      }
    } catch (error) {
      showInfoToast("Error uploading file!");
    } finally {
      setIsLoading(false);
      setUploadingBatch(null);
    }
  };

  const handlePreview = async (doc: any) => {
    try {
      const res = await getDocument(doc.claim_number, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to fetch document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      setSelectedDoc({ ...doc, file_url: url });
      setOpenPreview(true);
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const handlePopDownload = async (doc: any) => {
    try {
      const res = await getDocument(doc.claim_number, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  useEffect(() => {
    fetchReportHistory();
  }, [!isDataFiltered]);

  return (
    <Box
      overflow="hidden"
      p={{ xs: 1, sm: 2, md: 3 }}
      bgcolor="background.paper"
      height={{ xs: "auto", md: "100vh" }}
      display="flex"
      flexDirection="column"
      sx={{
        boxSizing: "border-box",
      }}
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}
      <Box flexShrink={0} sx={{ marginTop: { xs: 7, sm: 0 }, mb: 3 }}>
        <Typography
          fontSize={{ xs: 18, sm: 22, md: 25 }}
          fontWeight={600}
          color="primary.main"
          textAlign="left"
        >
          Finance/Payment History
        </Typography>
      </Box>
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: 2,
          background: "#ffffff",
        }}
        elevation={1}
      >
        <Box textAlign="right">
          <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
            Filter Mode
          </Typography>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(e, newValue) =>
              newValue && setFilterMode(newValue || "single")
            }
            size="small"
            color="primary"
          >
            <ToggleButton value="today">Today</ToggleButton>
            {/* <ToggleButton value="single">Single</ToggleButton> */}
            <ToggleButton value="range">Range</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Grid
          container
          spacing={2}
          alignItems="center"
          justifyContent="flex-end"
        >
          <Grid size={{ xs: 12, md: 3 }}>
            <Input
              select
              fullWidth
              label="Select Report Type"
              variant="outlined"
              value={reportType}
              onChange={(e: any) => setReportType(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (selected: any) =>
                  selected ? (
                    selected
                  ) : (
                    <span style={{ color: "#aaa" }}>Report Type</span>
                  ),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: "#ffffff",
                      borderRadius: "12px",
                      maxHeight: "300px",
                    },
                  },
                },
              }}
            >
              <MenuItem
                value="Cash Payout"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                Cash Payout
              </MenuItem>
              <MenuItem
                value="Service Payout"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                Service Payout
              </MenuItem>
              <MenuItem
                value="10% Payout"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                10% Payout
              </MenuItem>
              <MenuItem
                value="ALL Claims"
                sx={{
                  background: "background.default",
                  borderBottom: `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                All Claims
              </MenuItem>
            </Input>
          </Grid>

          {filterMode === "range" ? (
            <>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  type="date"
                  label="From Date"
                  variant="outlined"
                  value={startDate}
                  onChange={(e: any) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  type="date"
                  label="To Date"
                  variant="outlined"
                  value={endDate}
                  onChange={(e: any) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: startDate }}
                />
              </Grid>
            </>
          ) : filterMode === "today" ? null : (
            <Grid size={{ xs: 12, md: 3 }}>
              <Input
                type="date"
                label="Select Date"
                placeholder="DD-MM-YYYY"
                variant="outlined"
                value={date}
                onChange={(e: any) => setDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}

          <Grid size={{ xs: 12, sm: 4, md: 2 }} mt={3}>
            {isDataFiltered ? (
              <ButtonComp
                variant="contained"
                color="primary"
                width={150}
                onClick={() => {
                  setReportType("");
                  setDate("");
                  setStartDate("");
                  setEndDate("");
                  setFilterMode("single");
                  setIsDataFiltered(false);
                  setPage(0);

                  // Fetch all data (unfiltered)
                  fetchReportHistory(0);
                }}
              >
                Clear filter
              </ButtonComp>
            ) : (
              <ButtonComp
                variant="contained"
                color="primary"
                width={150}
                onClick={() => {
                  setIsDataFiltered(true);
                  if (
                    filterMode === "range" &&
                    startDate &&
                    endDate &&
                    new Date(startDate) > new Date(endDate)
                  ) {
                    toast.error("From Date cannot be after To Date!");
                    return;
                  }
                  setPage(0);
                  fetchReportHistory(0);
                }}
              >
                Filter
              </ButtonComp>
            )}
          </Grid>
        </Grid>
      </Paper>
      <Box
        mt={4}
        flex={1}
        overflow="auto"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Paper sx={{ background: "#ffffff", overflowX: "auto" }} elevation={2}>
          <TableContainer
            sx={{
              maxHeight: 420,
              overflowX: "auto",
            }}
          >
            <Table sx={{ width: "100%" }}>
              <TableHead
                sx={{
                  minWidth: "max-content",
                  "& .MuiTableCell-root": {
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableRow>
                  <TableCell>BATCH NO</TableCell>
                  <TableCell>CATEGORY</TableCell>
                  <TableCell>GENERATED ON</TableCell>
                  <TableCell>BATCHED DATE</TableCell>
                  <TableCell>REPORT TYPE</TableCell>
                  <TableCell>GENERATED BY</TableCell>
                  <TableCell>REPORT LINK</TableCell>
                  <TableCell>PROOF OF PAYMENT</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {!noData && reports.length > 0 ? (
                  reports.map((item: any, index: any) => (
                    <TableRow key={index}>
                      <TableCell>{item.batchNo}</TableCell>
                      <TableCell>{item.category}</TableCell>
                      <TableCell>
                        {item.createdAt
                          ? dayjs(item.createdAt).format("YYYY-MM-DD")
                          : ""}
                      </TableCell>
                      <TableCell>{item.reportDate}</TableCell>
                      <TableCell>{item.reportType}</TableCell>
                      <TableCell>{item.generatedBy}</TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            color: "primary.main",
                            cursor: "pointer",
                            textDecoration: "none",
                          }}
                          onClick={() => handleReportDownload(item.reportLink)}
                        >
                          View Report
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {item.uploaded_attachments &&
                        item.uploaded_attachments.length > 0 ? (
                          <Link
                            underline="none"
                            color="primary.main"
                            sx={{ cursor: "pointer" }}
                            fontSize="medium"
                            onClick={() =>
                              handlePreview({ ...item.uploaded_attachments[0] })
                            }
                          >
                            View POP
                          </Link>
                        ) : (
                          <Link
                            underline="none"
                            sx={{ cursor: "pointer" }}
                            color="primary.main"
                            fontSize="medium"
                            onClick={() => {
                              setShowBulkPayment(true);
                              setSelectedBatchNo(item.batchNo);
                              setType(item.reportType);
                            }}
                          >
                            Attach POP
                          </Link>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                      No data to display
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      {/* Pagination */}
      {reportsTotalCount > pageSize && (
        <Box display="flex" justifyContent="center" p={2}>
          <Pagination
            count={Math.ceil(reportsTotalCount / pageSize)}
            page={page + 1}
            onChange={(_, page) => callNextPage(page)}
            siblingCount={0}
            boundaryCount={1}
            variant="outlined"
            shape="rounded"
            color="primary"
            renderItem={(item) => (
              <PaginationItem
                {...item}
                components={{
                  next: () => <span style={{ padding: "0 8px" }}>Next</span>,
                }}
              />
            )}
          />
        </Box>
      )}

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handlePopDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {showBulkPayment && (
        <BulkPaymentPopup
          open={showBulkPayment}
          onClose={() => setShowBulkPayment(false)}
          getPopFile={getPopFile}
          popFileName={popFileName}
          onSubmit={handleFileUpload}
          batchNo={selectedBatchNo}
          type={type}
        />
      )}
    </Box>
  );
}
